import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import {
  ArrowRight,
  CalendarDays,
  Gamepad2,
  Gift,
  LayoutDashboard,
  Plus,
  Settings,
  Users,
} from "lucide-react"
import { api } from "../../../../convex/_generated/api"
import { useWildGuessSoloEnabled } from "@/lib/analytics/posthog"

export const Route = createFileRoute("/_authed/admin/")({
  component: AdminOverview,
})

function AdminOverview() {
  const stats = useQuery(api.stats.systemOverview)
  const soloEnabled = useWildGuessSoloEnabled()

  return (
    <div className="space-y-5">
      <section className="console-panel rounded-[1.35rem] p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="console-kicker">Admin dashboard</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight">Operations</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[color:var(--muted-foreground)]">
              Control events, live games, users, rewards, and system settings from one place.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <QuickAction to="/host/events/new" label="New event" icon={Plus} />
            <QuickAction to="/admin/events" label="Events" icon={CalendarDays} />
            <QuickAction to="/admin/users" label="Users" icon={Users} />
            <QuickAction to="/admin/settings" label="Settings" icon={Settings} />
          </div>
        </div>
      </section>

      {stats === undefined ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="console-panel rounded-[1.25rem] p-4">
              <div className="h-3 w-24 animate-pulse rounded bg-[color:var(--muted)]" />
              <div className="mt-4 h-8 w-14 animate-pulse rounded bg-[color:var(--muted)]" />
              <div className="mt-3 h-3 w-32 animate-pulse rounded bg-[color:var(--muted)]" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={CalendarDays}
            label="Active events"
            value={stats.events.active}
            sub={`${stats.events.completed} completed`}
            href="/admin/events"
          />
          <StatCard
            icon={Gamepad2}
            label="Live games"
            value={stats.games.active}
            sub={[
              `${stats.games.scheduled} scheduled`,
              `${stats.games.ready} ready`,
              `${stats.games.completed} done`,
            ].join(" | ")}
            href="/admin/games"
          />
          <StatCard
            icon={Users}
            label="Teams"
            value={stats.teams.total}
            sub="non-deleted"
          />
          <StatCard
            icon={Gift}
            label="Submissions"
            value={stats.submissions.total}
            sub="across all rounds"
          />
        </div>
      )}

      <div className="console-panel rounded-[1.35rem] border-dashed p-4 text-sm leading-6 text-[color:var(--muted-foreground)]">
        {soloEnabled
          ? "Player-facing Wild Guess Solo and Lyrically Challenged flows now live in the native apps. This web app is for admin and host operations only."
          : "Lyrically Challenged is the active player focus. This web app is for admin and host operations only."}
      </div>
    </div>
  )
}

function QuickAction({
  to,
  label,
  icon: Icon,
}: {
  to: string
  label: string
  icon: typeof LayoutDashboard
}) {
  return (
    <Link
      to={to}
      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm font-bold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
    >
      <Icon className="h-4 w-4 text-[color:var(--primary)]" aria-hidden />
      {label}
    </Link>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  href,
}: {
  icon: typeof LayoutDashboard
  label: string
  value: number
  sub?: string
  href?: string
}) {
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
          {label}
        </p>
        <span className="flex h-8 w-8 items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-soft)] text-[color:var(--primary)]">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
      </div>
      <p className="mt-2 text-3xl font-bold tracking-tight tabular-nums">{value}</p>
      {sub && (
        <p className="mt-1 text-[11px] text-[color:var(--muted-foreground)]">
          {sub}
        </p>
      )}
      {href && (
        <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[color:var(--primary)]">
          Open
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </span>
      )}
    </>
  )
  if (href) {
    return (
      <Link
        to={href}
        className="console-panel block rounded-[1.25rem] p-4 transition-colors hover:bg-[color:var(--surface-hover)]"
      >
        {inner}
      </Link>
    )
  }
  return (
    <div className="console-panel rounded-[1.25rem] p-4">
      {inner}
    </div>
  )
}
